import { AzureAvatarService } from "@service/avatar/azure-avatar.service";
import { useDispatch } from "react-redux";
import { MutableRefObject, useCallback, useRef } from "react";
import { useDebounceProcessing } from "@hooks/useDebounceProcessing";
import { setTalking } from "@store/avatarSlice";
import { useCompanionStreamingService } from "@hooks/useCompanionStreamingService";
import { EventType } from '@models/audio-message';

const useStreaming = ({
  sessionId,
  avatar,
}: {
  sessionId: string;
  avatar: MutableRefObject<AzureAvatarService | null>;
}) => {
  const dispatch = useDispatch();
  const speakQueueSize = useRef(0);
  const { setValue: setProcessing } = useDebounceProcessing();

  const speak = useCallback(
    async (text: string) => {
      if (!avatar.current?.running) {
        return;
      }
      speakQueueSize.current = speakQueueSize.current + 1;
      setProcessing(true, true);
      dispatch(setTalking(true));
      try {
        await avatar.current.speak(text);
      } catch (e) {
        console.error(e);
      } finally {
        dispatch(setTalking(false));
        speakQueueSize.current = speakQueueSize.current - 1;
        if (!speakQueueSize.current) {
          setProcessing(false, true);
        }
      }
    },
    [dispatch, setProcessing, avatar],
  );

  const { send } = useCompanionStreamingService(speak);

  const sendMessage = useCallback(
    async (audio?: ArrayBufferLike, photoId?: string, eventType?: EventType): Promise<void> => {
      try {
        await send({
          audio: audio || null,
          sessionId,
          photoId: photoId || null,
          eventType: eventType || null,
        });
      } catch (e) {
        console.error(e);
      }
    },
    [sessionId, send],
  );

  return sendMessage;
};

export default useStreaming;
